// VoiceServicesCard — OS-wide local voice-service lights for the Console.
// Props in only: renders the current server probe sample, never a cached last-good state.

import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import { RexyVoiceServiceLight, RexyVoiceServices } from '../hooks/useRexySocket';

const MONO = REXY_FONT;
const GOOD = '#00FF9C';

const SERVICE_ORDER = [
  { id: 'vllm', label: 'vLLM', port: 8000 },
  { id: 'b_tts', label: 'B-TTS', port: 8754 },
  { id: 'denoiser', label: 'Denoiser', port: 8753 },
  { id: 'daemon_ctl', label: 'Daemon ctl', port: 8765 },
  // The media edge (:8766) — WebRTC in its own process, because aiortc cannot live
  // inside the eventlet-patched Flask process. Listed here so the seventh child has a
  // light at all: this order is hardcoded, so a probe absent from it renders as
  // nothing rather than as down.
  { id: 'media_edge', label: 'Media edge', port: 8766 },
  { id: 'recall', label: 'Recall', port: 5099 },
] as const;

function normalizeStatus(status: string | undefined): string {
  const s = String(status || 'down').toLowerCase();
  if (s === 'ok') return 'ok';
  if (s === 'warming') return 'warming';
  return 'down';
}

function statusLabel(status: string): string {
  switch (status) {
    case 'ok':
      return 'READY';
    case 'warming':
      return 'WARMING';
    case 'down':
    default:
      return 'DOWN';
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'ok':
      return GOOD;
    case 'warming':
      return REXY_CONSOLE.warning;
    case 'down':
    default:
      return REXY_CONSOLE.critical;
  }
}

function serviceMap(services: RexyVoiceServices | null): Record<string, RexyVoiceServiceLight> {
  const rows: Record<string, RexyVoiceServiceLight> = {};
  for (const svc of services?.services || []) rows[svc.id] = svc;
  return rows;
}

function LightRow({ expected, svc }: {
  expected: typeof SERVICE_ORDER[number];
  svc?: RexyVoiceServiceLight;
}) {
  const status = normalizeStatus(svc?.status);
  const color = statusColor(status);
  const detail = svc?.detail || 'NO CURRENT SAMPLE';
  return (
    <div style={styles.row}>
      <div style={styles.rowTop}>
        <span style={styles.name}>{expected.label} :{expected.port}</span>
        {/* A COLOURED LIGHT — GREEN / AMBER / RED — in place of the status word.
            AMBER IS NOT DECORATION: B-TTS answers 000 for ~37s after every recycle
            while Qwen3-TTS loads, and the daemon serves throughout. Red for those
            37 seconds would be the light asserting something untrue. The three-state
            vocabulary already ran end to end; this only changes how it is drawn.
            The word survives as the accessible name, so nothing is lost to colour. */}
        <span
          style={{ ...styles.light, background: color, boxShadow: `0 0 6px ${color}` }}
          role="img"
          aria-label={statusLabel(status)}
          title={statusLabel(status)}
        />
      </div>
      <div style={styles.metaRow}>
        {/* The detail line stays — MODEL READY, ANSWERED, READY. `ANSWERED` on
            daemon_ctl is what distinguishes a control plane that replied from a
            socket that merely accepted. */}
        <span style={styles.detail}>{detail}</span>
      </div>
    </div>
  );
}

export interface VoiceServicesCardProps {
  services: RexyVoiceServices | null;
}

export default function VoiceServicesCard({ services }: VoiceServicesCardProps) {
  if (!services) {
    return (
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.sectionHeader}>║ VOICE SERVICES</div>
          <div style={{ ...styles.overall, color: REXY_CONSOLE.mutedText }}>NO PAYLOAD</div>
        </div>
        <div style={styles.nullState}>No current voice-services sample.</div>
      </div>
    );
  }
  const rows = serviceMap(services);
  const overall = normalizeStatus(services.overall);
  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.sectionHeader}>║ VOICE SERVICES</div>
        <div style={{ ...styles.overall, color: statusColor(overall) }}>{statusLabel(overall)}</div>
      </div>
      <div style={styles.rows}>
        {SERVICE_ORDER.map((expected) => (
          <LightRow key={expected.id} expected={expected} svc={rows[expected.id]} />
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 12,
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 0,
    color: REXY_CONSOLE.primary,
  },
  overall: {
    fontFamily: MONO,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0,
    whiteSpace: 'nowrap',
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  row: {
    borderTop: `1px solid ${REXY_CONSOLE.rowDivider}`,
    paddingTop: 8,
  },
  rowTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  name: {
    fontFamily: MONO,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0,
    color: REXY_CONSOLE.primary,
    minWidth: 0,
  },
  status: {
    fontFamily: MONO,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0,
    whiteSpace: 'nowrap',
  },
  light: {
    width: 9,
    height: 9,
    borderRadius: '50%',
    flexShrink: 0,
    alignSelf: 'center',
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    marginTop: 3,
  },
  nullState: {
    fontFamily: MONO,
    fontSize: 11,
    color: REXY_CONSOLE.mutedText,
  },
  detail: {
    fontFamily: MONO,
    fontSize: 10,
    color: REXY_CONSOLE.mutedText,
    textAlign: 'right',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
